import { useState } from "react";
import { Brain, Mail, KeyRound, X, Loader2, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";
import { api } from "./api";
import { C, ui } from "./theme";
import { T, t } from "./i18n.jsx";

/* ------------------------------------------------------------------ *
 * AuthPanel — register / sign-in for the app. Two paths:
 *   • Continue with Google (OAuth redirect via the gateway)
 *   • Email + password, verified by a 6-digit code the gateway emails
 *     over SMTP (register → verify → in; plus forgot/reset).
 * The gateway JWT never reaches the browser — verify/login set an
 * httpOnly session cookie server-side; on success we call onAuthed().
 *   props: onClose(), onAuthed()
 * ------------------------------------------------------------------ */
export default function AuthPanel({ onClose, onAuthed }) {
  const [mode, setMode] = useState("login"); // login|register|verify|forgot|reset
  const [f, setF] = useState({ name: "", email: "", password: "", code: "", newPassword: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const go = (m) => { setMode(m); setErr(null); setInfo(null); };
  const run = (fn, after) => {
    setBusy(true); setErr(null);
    fn().then((r) => { setBusy(false); after && after(r); })
      .catch((e) => { setBusy(false); setErr(cleanErr(e.message)); });
  };

  const submit = () => {
    if (mode === "login") run(() => api.passwordLogin(f.email.trim(), f.password), () => onAuthed());
    else if (mode === "register") {
      if (!f.email.trim() || f.password.length < 8) return setErr(t("Enter an email and a password of at least 8 characters."));
      run(() => api.register(f.email.trim(), f.password, f.name.trim() || undefined), () => { go("verify"); setInfo(t("We emailed a 6-digit code to {email}.", { email: f.email.trim() })); });
    } else if (mode === "verify") run(() => api.verifyEmail(f.email.trim(), f.code.trim()), () => onAuthed());
    else if (mode === "forgot") run(() => api.forgotPassword(f.email.trim()), () => { go("reset"); setInfo(t("If that email is registered, a reset code is on its way.")); });
    else if (mode === "reset") {
      if (f.newPassword.length < 8) return setErr(t("New password must be at least 8 characters."));
      run(() => api.resetPassword(f.email.trim(), f.code.trim(), f.newPassword), () => { go("login"); setInfo(t("Password reset — sign in with your new password.")); });
    }
  };

  const TITLES = { login: t("Sign in"), register: t("Create your account"), verify: t("Check your email"), forgot: t("Reset your password"), reset: t("Set a new password") };

  return (
    <div style={s.overlay} onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} .spin{animation:spin .9s linear infinite} .auth-in:focus{border-color:${C.indigo}!important}`}</style>
      <div style={s.modal}>
        <div style={s.head}>
          <div style={s.logo}><Brain size={15} color={C.bg} /></div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>Onset</div>
          <button style={{ ...ui.btnGhost, marginInlineStart: "auto", padding: "4px 8px" }} onClick={onClose} aria-label={t("Close")}><X size={14} /></button>
        </div>

        <div style={{ padding: 18 }}>
          {(mode === "verify" || mode === "reset" || mode === "forgot") && (
            <button style={s.back} onClick={() => go(mode === "forgot" ? "login" : mode === "verify" ? "register" : "forgot")}><ArrowLeft size={13} /> <T>Back</T></button>
          )}
          <div style={{ fontSize: 18, fontWeight: 800, color: C.hi, marginBottom: 4 }}>{TITLES[mode]}</div>
          <div style={{ color: C.mid, fontSize: 13, marginBottom: 14 }}>
            {mode === "login" && <T>Welcome back. Use email &amp; password, or continue with Google.</T>}
            {mode === "register" && <T>We'll email you a 6-digit code to confirm your address.</T>}
            {mode === "verify" && <span>{t("Enter the 6-digit code sent to {email}.", { email: f.email.trim() })}</span>}
            {mode === "forgot" && <T>Enter your email and we'll send a reset code.</T>}
            {mode === "reset" && <T>Enter the code we emailed and choose a new password.</T>}
          </div>

          {info && <div style={s.info}><CheckCircle2 size={13} color={C.green} /> <span>{info}</span></div>}
          {err && <div style={s.err}><AlertTriangle size={13} color={C.red} /> <span>{err}</span></div>}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {mode === "register" && <In icon={null} label={t("Name")} value={f.name} onChange={set("name")} placeholder={t("optional")} />}
            {(mode === "login" || mode === "register" || mode === "forgot" || mode === "reset") && (
              <In type="email" label={t("Email")} value={f.email} onChange={set("email")} placeholder="name@example.com" />
            )}
            {(mode === "login" || mode === "register") && (
              <In type="password" label={t("Password")} value={f.password} onChange={set("password")} placeholder={mode === "register" ? t("at least 8 characters") : ""} onEnter={submit} />
            )}
            {(mode === "verify" || mode === "reset") && (
              <In label={t("6-digit code")} value={f.code} onChange={set("code")} placeholder="123456" mono onEnter={mode === "verify" ? submit : undefined} />
            )}
            {mode === "reset" && <In type="password" label={t("New password")} value={f.newPassword} onChange={set("newPassword")} placeholder={t("at least 8 characters")} onEnter={submit} />}
          </div>

          <button style={{ ...ui.btn, background: C.indigo, color: "#06102e", width: "100%", justifyContent: "center", padding: "10px", marginTop: 14, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>
            {busy ? <Loader2 size={15} className="spin" /> : <KeyRound size={15} />}
            <span>{mode === "login" ? t("Sign in") : mode === "register" ? t("Create account") : mode === "verify" ? t("Verify & continue") : mode === "forgot" ? t("Send reset code") : t("Reset password")}</span>
          </button>

          {mode === "verify" && (
            <button style={s.linkBtn} disabled={busy} onClick={() => run(() => api.resendCode(f.email.trim()), () => setInfo(t("Code resent.")))}><T>Resend code</T></button>
          )}

          {(mode === "login" || mode === "register") && (
            <>
              <div style={s.or}><span style={s.orLine} /><span style={{ color: C.dim, fontSize: 11 }}>{t("or")}</span><span style={s.orLine} /></div>
              <button style={s.google} onClick={() => api.login()}>
                <GoogleG /> <T>Continue with Google</T>
              </button>
            </>
          )}

          {/* mode switches */}
          <div style={{ marginTop: 14, textAlign: "center", color: C.mid, fontSize: 13 }}>
            {mode === "login" && <>
              <button style={s.inlineLink} onClick={() => go("forgot")}><T>Forgot password?</T></button>
              <span style={{ margin: "0 8px", color: C.dim }}>·</span>
              <button style={s.inlineLink} onClick={() => go("register")}><T>Create an account</T></button>
            </>}
            {mode === "register" && <><T>Already have an account?</T> <button style={s.inlineLink} onClick={() => go("login")}><T>Sign in</T></button></>}
            {(mode === "forgot" || mode === "reset") && <button style={s.inlineLink} onClick={() => go("login")}><T>Back to sign in</T></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function In({ label, type = "text", value, onChange, placeholder, mono, onEnter }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ color: C.dim, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <input className="auth-in" type={type} value={value} onChange={onChange} placeholder={placeholder}
        onKeyDown={(e) => { if (e.key === "Enter" && onEnter) onEnter(); }}
        style={{ ...ui.input, padding: "9px 11px", fontFamily: mono ? "var(--mono)" : "var(--ui)", letterSpacing: mono ? 3 : 0 }} />
    </label>
  );
}

function GoogleG() {
  return (
    <svg width="15" height="15" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 0-24c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 1 0 24 44a20 20 0 0 0 19.6-23.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 0 0 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.6 5.1A20 20 0 0 0 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2C39.9 36 44 30.6 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}

function cleanErr(m) {
  const s = String(m || "");
  const i = s.indexOf("· ");
  return (i >= 0 ? s.slice(i + 2) : s).replace(/^POST [^ ]+ → \d+\s*/, "") || "Something went wrong.";
}

const s = {
  overlay: { position: "fixed", inset: 0, background: "rgba(6,9,13,0.86)", display: "grid", placeItems: "center", zIndex: 70, padding: 20 },
  modal: { width: "min(420px, 100%)", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.5)" },
  head: { display: "flex", alignItems: "center", gap: 9, padding: "12px 16px", borderBottom: `1px solid ${C.border}`, color: C.hi },
  logo: { width: 26, height: 26, borderRadius: 7, background: `linear-gradient(135deg, ${C.indigo}, ${C.purple})`, display: "grid", placeItems: "center" },
  back: { display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", color: C.mid, cursor: "pointer", fontSize: 12, padding: 0, marginBottom: 8, fontFamily: "var(--ui)" },
  info: { display: "flex", gap: 7, alignItems: "center", background: "rgba(79,180,119,0.1)", border: `1px solid ${C.green}44`, borderRadius: 8, padding: "8px 10px", color: C.mid, fontSize: 12.5, marginBottom: 10, lineHeight: 1.45 },
  err: { display: "flex", gap: 7, alignItems: "center", background: "rgba(229,100,91,0.1)", border: `1px solid ${C.red}44`, borderRadius: 8, padding: "8px 10px", color: C.hi, fontSize: 12.5, marginBottom: 10, lineHeight: 1.45 },
  or: { display: "flex", alignItems: "center", gap: 10, margin: "14px 0" },
  orLine: { flex: 1, height: 1, background: C.border },
  google: { display: "flex", alignItems: "center", justifyContent: "center", gap: 9, width: "100%", padding: "10px", borderRadius: 9, border: `1px solid ${C.border}`, background: C.raised, color: C.hi, fontWeight: 600, fontSize: 13.5, cursor: "pointer", fontFamily: "var(--ui)" },
  linkBtn: { display: "block", margin: "10px auto 0", background: "none", border: "none", color: C.indigo, cursor: "pointer", fontSize: 12.5, fontFamily: "var(--ui)" },
  inlineLink: { background: "none", border: "none", color: C.indigo, cursor: "pointer", fontSize: 13, fontWeight: 600, padding: 0, fontFamily: "var(--ui)" },
};
